import type { VideoMeta } from "../types/video";

/**
 * 集型 × 自動編號（與 App lib/core/models/episode_numbering.dart 同一套規則）。
 *
 * 把「播放序（order）」「角色（episode_type）」「顯示文字（episode_label）」分離：
 * - 正片(normal)：用正片序編號（第 1、2、3 集），跳過 SP/預告
 * - 特番(special)：SP1、SP2…，不佔正片序
 * - 預告(trailer)：預告 / 預告1、預告2…
 * 顯示優先序：手動 episode_label > 依集型自動編號。
 */
export type EpisodeType = "normal" | "special" | "trailer";

export function parseEpisodeType(raw: unknown): EpisodeType {
  if (raw === "special" || raw === "trailer") return raw;
  return "normal";
}

export interface EpisodeDisplayInfo {
  type: EpisodeType;
  rank: number; // 同型別內 1-based 序
  label?: string | null; // 手動覆寫（僅影響 App 選集選單，不影響此 display）
  trailerCount: number;
  /** 集數欄顯示文字（型別自動編號，不吃 label）："第 2 集" / "SP1" / "預告" */
  display: string;
}

/** 依「位置序」排好的清單算每集顯示資訊。輸入須已照 order 排序。 */
export function computeEpisodeDisplay(
  ordered: Pick<VideoMeta, "episode_type" | "episode_label">[]
): EpisodeDisplayInfo[] {
  const trailerCount = ordered.filter(
    (v) => parseEpisodeType(v.episode_type) === "trailer"
  ).length;

  let normalRank = 0;
  let specialRank = 0;
  let trailerRank = 0;
  return ordered.map((v) => {
    const type = parseEpisodeType(v.episode_type);
    let rank: number;
    if (type === "normal") rank = ++normalRank;
    else if (type === "special") rank = ++specialRank;
    else rank = ++trailerRank;

    // 集數欄一律用型別自動編號，不吃 episode_label（label 只影響 App 選集選單）
    let display: string;
    if (type === "normal") display = `第 ${rank} 集`;
    else if (type === "special") display = `SP${rank}`;
    else display = trailerCount > 1 ? `預告${rank}` : "預告";

    return { type, rank, label: v.episode_label, trailerCount, display };
  });
}
